/**
 * Payment Success Page - Handles return from Instamojo
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight, Shield } from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const paymentRequestId = searchParams.get('payment_request_id');
    const paymentId = searchParams.get('payment_id');

    if (paymentRequestId) {
      checkPaymentStatus(paymentRequestId);
    } else {
      setStatus('error');
      setError('No payment information found');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (paymentRequestId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${paymentRequestId}`
      );

      setPaymentDetails(response.data);

      if (response.data.status === 'completed') {
        setStatus('success');
      } else if (response.data.status === 'failed') {
        setStatus('failed');
        setError('Your payment could not be processed.');
      } else {
        // Still pending, poll again
        setTimeout(() => checkPaymentStatus(paymentRequestId), 3000);
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to verify payment');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <Navigation />
        <div className="pt-28 pb-20 px-4">
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-blue-200 shadow-xl">
              <CardContent className="p-8 text-center">
                <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h1>
                <p className="text-gray-600">Please wait while we confirm your payment...</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        <Navigation />
        <div className="pt-28 pb-20 px-4">
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-green-200 shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
                
                <p className="text-lg text-gray-600 mb-6">
                  Thank you for your payment. Your subscription is now active.
                </p>

                {paymentDetails && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount Paid</span>
                        <span className="font-semibold text-gray-900">₹{paymentDetails.amount}</span>
                      </div>
                      {paymentDetails.payment_id && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Transaction ID</span>
                          <span className="font-mono text-xs text-gray-700">{paymentDetails.payment_id}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 text-left">
                  <h3 className="font-semibold text-blue-900 mb-3">What's Next?</h3>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">1</span>
                      We'll assign a dedicated observer to your child
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">2</span>
                      You'll receive a call to confirm the session schedule
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">3</span>
                      First session begins - we call your child at the scheduled time!
                    </li>
                  </ul>
                </div>

                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
                  <Shield className="w-4 h-4" />
                  <span>Your data is secure and protected</span>
                </div>

                <Button
                  onClick={() => {
                    const enrollToken = localStorage.getItem('enroll_token');
                    if (enrollToken) {
                      localStorage.setItem('parent_token', enrollToken);
                    }
                    navigate('/parent/dashboard');
                  }}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg w-full"
                >
                  Go to Parent Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Error or Failed state
  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
      <Navigation />
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-md mx-auto">
          <Card className="border-2 border-red-200 shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {status === 'failed' ? 'Payment Failed' : 'Error'}
              </h1>
              
              <p className="text-lg text-gray-600 mb-6">
                {error || 'Something went wrong with your payment.'}
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/enroll/payment')}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 w-full"
                >
                  Try Again
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => navigate('/enroll')}
                  className="w-full"
                >
                  Back to Plans
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
